/**
 * The rules about a finding — pure logic, without DOM.
 *
 * Here stands what a finding IS: a missing comma, a missing space, a wrongly
 * written word. Everything visible (the card, the waves, the wedges) asks
 * here and draws accordingly. That is why it stands for itself: it can be
 * checked without a browser having to lay out text.
 */

import * as store from '../editor/store.js'
import type { Finding, Insertion } from '../types.js'

// --- Commas and spaces -------------------------------------------------

/**
 * Rules that are about a comma.
 *
 * The rule name decides first, because it also holds where LanguageTool gives
 * no suggestion at all — it marks the whole clause and leaves the comma to
 * the writer. Those are exactly the findings that isPunctuation() cannot
 * recognize, and they belong with the others.
 */
export const COMMA_RULES = /^(KOMMA_|SUBJUNKTION_KOMMA|INDIREKTE_FRAGE|COMMA_)/

/**
 * Conjunctions before which a comma goes.
 *
 * Needed for the comma rules that deliver NO suggestion: LanguageTool marks
 * the whole clause there and leaves the position open. But it is derivable —
 * the comma goes before the word that begins the second clause.
 *
 * Only used when exactly ONE of these words stands in the marked range. With
 * several, the place is genuinely ambiguous, and then it is better to mark
 * the clause than to point at the wrong spot.
 *
 * Deliberately WITHOUT der/die/das: they are articles far more often than
 * relative pronouns. In "kenne die Geschichte nicht mehr doch ich weiß …"
 * the "die" of "die Geschichte" would count as a second candidate and the
 * place would be given up, although "doch" is unambiguous.
 */
export const COMMA_CONJUNCTIONS =
  /\b(doch|aber|denn|sondern|weil|dass|wenn|als|obwohl|damit|bevor|nachdem|während|indem|sodass|falls|ob)\b/g

/**
 * The place for a comma rule that gives no suggestion.
 */
export function clauseComma(hit: Finding | null | undefined): Insertion | null {
  if (!hit?.word || !COMMA_RULES.test(hit.rule ?? '')) return null
  COMMA_CONJUNCTIONS.lastIndex = 0
  const hits: number[] = []
  let m
  while ((m = COMMA_CONJUNCTIONS.exec(hit.word))) {
    // Not at the very beginning: there the comma would land in front of the
    // marked range, and LanguageTool would have said so itself.
    if (m.index > 0) hits.push(m.index)
  }
  if (hits.length !== 1) return null
  // Exactly one entry, checked just above.
  return { offset: hits[0]! - 1, char: ',' }
}

/**
 * Where does a single character have to be INSERTED — and which one?
 *
 * This is the shape of nearly every punctuation finding: the letters stay as
 * they are, one character comes in between. LanguageTool marks the
 * surrounding words for it, and a wave under them says "these words are
 * wrong" — which is exactly what they are not. "ablenkt den" is marked, but
 * both words are correct; the comma belongs between them.
 *
 * The place is found by comparing the suggestion with the marked text: the
 * common beginning ends exactly where the character goes.
 *
 * @returns  offset relative to the start of the finding, and the character
 *   itself. null if it is not a pure insertion.
 */
export function insertionAt(hit: Finding | null | undefined): Insertion | null {
  const target = hit?.replacements?.[0]
  const word = hit?.word
  if (!word) return null
  // No suggestion, but a comma rule: derive the place from the clause.
  if (!target) return clauseComma(hit)
  // Exactly one character longer, and the rest identical: then it is an
  // insertion and not a rewriting.
  if (target.length !== word.length + 1) return null
  let i = 0
  while (i < word.length && word[i] === target[i]) i++
  if (word.slice(i) !== target.slice(i + 1)) return null
  // i < target.length, because target is one character longer than word and
  // the loop stops at word.length at the latest.
  const char = target[i]!
  // Only spaces and commas. A letter that is missing is a spelling mistake
  // and belongs under the word, not into the gap.
  if (char !== ' ' && char !== ',') return null
  return { offset: i, char }
}

/**
 * Rules about spaces. They are NOT comma mistakes.
 *
 * A missing space after a full stop ("nicht.Aber") is not a question of
 * punctuation that one can argue about — it is plainly wrong, easy to see and
 * easy to put right. It stays red even with the commas switched off. Where a
 * comma goes needs the sentence structure; where a space goes does not.
 */
export const SPACE_RULES = /^(DE_SENTENCE_WHITESPACE|DOPPELTES_LEERZEICHEN|WHITESPACE_)/

/**
 * A space where none belongs — „Er kam , und ging".
 *
 * The other half of the same thing. Children who have not got the hang of it
 * put the space on the wrong side of the mark, and both halves are equally
 * plain: one does not argue about where a space goes, the way one argues
 * about a comma.
 *
 * It used to fall under the comma rules and disappeared with them, although
 * it has nothing to do with whether a comma belongs there.
 */
export const SPACE_BEFORE_RULE = /^COMMA_PARENTHESIS_WHITESPACE/

/** Is this finding about a space rather than about a comma? */
export const isSpaceFinding = (hit: Finding | null | undefined): boolean =>
  SPACE_RULES.test(hit?.rule ?? '') ||
  (SPACE_BEFORE_RULE.test(hit?.rule ?? '') && !!removalAt(hit))

/**
 * Where does a single character have to be TAKEN OUT — and which one?
 *
 * The mirror of `insertionAt`. „Er kam , und" is not a rewriting either: the
 * letters stay, one character goes. Without this the finding looked like a
 * rewriting and got no help card of its own.
 */
export function removalAt(hit: Finding | null | undefined): Insertion | null {
  const target = hit?.replacements?.[0]
  const word = hit?.word
  if (!word || target == null) return null
  // Exactly one character shorter, the rest identical.
  if (target.length !== word.length - 1) return null
  let i = 0
  while (i < target.length && word[i] === target[i]) i++
  if (word.slice(i + 1) !== target.slice(i)) return null
  const char = word[i]!
  // Only a space. A letter too many is a spelling mistake and belongs under
  // the word.
  if (char !== ' ') return null
  return { offset: i, char }
}

export function isCommaFinding(hit: Finding | null | undefined): boolean {
  if (!hit) return false
  if (isSpaceFinding(hit)) return false
  if (COMMA_RULES.test(hit.rule ?? '')) return true
  // The fallback by the suggestion also catches missing spaces — therefore
  // only what really adds a comma counts.
  const target = hit.replacements?.[0]
  if (!target) return false
  return isPunctuation(hit) && target.includes(',') && !hit.word.includes(',')
}

/**
 * Names what is missing, for the title of the help card.
 *
 * The title otherwise shows the marked word — but that word is correct here,
 * and showing it as a heading would say "this is wrong" about something that
 * is not.
 */
export function whatIsMissing(hit: Finding | null | undefined): string {
  const place = insertionAt(hit)
  if (place) {
    return place.char === ',' ? 'Hier fehlt ein Komma' : 'Hier fehlt ein Leerzeichen'
  }
  if (removalAt(hit)) return 'Hier steht ein Leerzeichen zu viel'
  const target = hit?.replacements?.[0] ?? ''
  const word = hit?.word ?? ''
  // Shorter than before: something goes away, it is not missing. The double
  // space is the case — two become one.
  if (target.length < word.length) return 'Ein Zeichen zu viel'
  return 'Hier fehlt ein Zeichen'
}

/**
 * The rule behind a spacing finding, in one sentence.
 *
 * Correcting it teaches nothing: the button puts the space right and the
 * next sentence has it wrong again. What is missing is the rule, and it is
 * one a twelve-year-old can hold on to — the mark sticks to the word in
 * front, the space comes after it.
 *
 * Only for spaces. Where a comma goes needs the sentence structure and does
 * not fit in a line.
 */
export function spacingRule(hit: Finding | null | undefined): string {
  const put = insertionAt(hit)
  if (put?.char === ' ') {
    return 'Nach einem Punkt oder Komma kommt immer ein Leerzeichen.'
  }
  if (removalAt(hit)) {
    return 'Das Satzzeichen klebt am Wort davor. Das Leerzeichen kommt erst danach.'
  }
  return ''
}

/**
 * Cuts out the neighbourhood of the insertion point.
 *
 * Keeps whole words and puts an ellipsis where something was cut away, so
 * that one sees it is an excerpt and not the whole sentence.
 */
export function around(
  word: string,
  target: string,
  at: number,
  span = 22
): [string, string] {
  if (word.length <= span * 2) return [word, target]
  let from = Math.max(0, at - span)
  let to = Math.min(word.length, at + span)
  if (from > 0) { const i = word.indexOf(' ', from); if (i >= 0 && i < at) from = i + 1 }
  if (to < word.length) { const i = word.lastIndexOf(' ', to); if (i > at) to = i }
  const pre = from > 0 ? '… ' : ''
  const post = to < word.length ? ' …' : ''
  return [
    pre + word.slice(from, to) + post,
    // From the insertion point onwards the target is one character longer.
    pre + target.slice(from, to + 1) + post,
  ]
}

/**
 * Shows the correction so that the added character is visible.
 *
 * A missing space is invisible in a line of text: " Aber" and "Aber" look the
 * same as a solution. The neighbouring characters give it something to stand
 * against — you see the gap only in contrast to what was there before.
 *
 * @param space  Mark EVERY space with ␣, not only the ones at the edges.
 *   Set where the space itself is the point: ". Aber" and ".Aber" are
 *   otherwise hard to tell apart in one line. Where a comma is the matter it
 *   stays off — there the comma is visible by itself, and "hat,␣trifft"
 *   would only be hard to read.
 */
export function preview(word: string, target: string, space = false): string {
  const show = (t: string) =>
    space
      ? t.replace(/ /g, '␣')
      : t.replace(/^ +/, (m) => '␣'.repeat(m.length))
         .replace(/ +$/, (m) => '␣'.repeat(m.length))
  return `${show(word)} → ${show(target)}`
}

/**
 * Is the mistake beside the word instead of in it?
 *
 * For a missing comma or a missing space after a full stop LanguageTool marks
 * the neighbouring word — "Aber" in "nicht.Aber". The word itself is
 * perfectly correct; only the character in front of it is missing.
 *
 * The whole help is built for misspelled words: it shows the word as the
 * title, asks "read it aloud, what do you hear?" and points at a wrong
 * letter. For a missing comma every one of those is wrong — there is no
 * letter to find and nothing to hear.
 *
 * Recognized by the suggestion: if it differs from the marked text only in
 * spaces and punctuation, the letters are right and the mistake is the
 * punctuation.
 */
export function isPunctuation(hit: Finding | null | undefined): boolean {
  if (!hit) return false
  const target = hit.replacements?.[0]
  if (!target) return false
  const bare = (t: string) => t.replace(/[\s,.;:!?]/g, '')
  return bare(target) === bare(hit.word) && target !== hit.word
}

// --- Words -----------------------------------------------------------------

/**
 * Is the finding about a WORD — "I do not know this one"?
 *
 * Only the speller of LanguageTool and the local fallback say that.
 * Everything else judges the sentence: a comma that is missing, a verb that
 * stands twice. Only a word finding gets the help with its stages; for the
 * rest the explanation decides.
 *
 * The category is not enough to tell them apart: DOPPELTES_VERB also runs
 * under 'TYPOS' although it judges the sentence.
 */
export const WORD_RULES = new Set(['GERMAN_SPELLER_RULE', 'LOCAL_SPELLER'])

export function isWordFinding(hit: Finding | null | undefined): boolean {
  return !!hit && WORD_RULES.has(hit.rule)
}

/**
 * Makes a usable hint out of a LanguageTool message.
 *
 * The messages contain typographic markup and sometimes name the solution
 * directly ("Meinten Sie „rausgehen"?") — at the hint stage that would be a
 * premature reveal. Such messages are discarded, so that the waiting time on
 * the solution button keeps its purpose.
 */
export function cleanMessage(msg: string | null | undefined): string | null {
  if (!msg) return null
  const plain = msg
    // LanguageTool mixes typographic quotation marks; unify them, otherwise
    // a half-opened quotation stands in the hint.
    .replace(/[„“”‚‘’«»]/g, '"')
    .replace(/<[^>]*>/g, '')
    .trim()
  // If the message already contains the correct word, it is not help but the
  // answer.
  if (/Meinten Sie|Möglicher Tippfehler|Rechtschreibfehler/i.test(plain)) return null
  return plain
}

// --- Detecting corrections -------------------------------------------------

/** Highest help stage reached per word, until it is corrected. */
const helperStages = new Map<string, number>()

/**
 * Records afterwards how far the help went with a word.
 *
 * The editor feeds this; it lies here because `solvedStage` reads it. The
 * highest stage wins — whoever has seen the solution has seen it.
 */
export function setStage(key: string, stage: number): void {
  helperStages.set(key, Math.max(helperStages.get(key) ?? 0, stage))
}

/**
 * Where a word that is still wrong came from.
 *
 * A correction is often two steps. Whoever fixes „baum" deletes the „b" and
 * then types the „B" — and in between, for a moment, „aum" stands there,
 * checked and underlined like any other mistake. Compared step by step, the
 * first step turned one mistake into another (nothing to record) and the
 * second turned „aum" into „Baum". That was recorded: fifteen times in one
 * afternoon, every capital he put right, as „aum → Baum", „tock → Stock",
 * „isen → Eisen" — and the real correction, „baum → Baum", nowhere.
 *
 * So the in-between word remembers the finding it came from, and the
 * correction is booked on that one.
 */
const origins = new Map<string, Finding>()

/** Another text, other words: everything remembered no longer holds. */
export function clearStages(): void {
  helperStages.clear()
  origins.clear()
}

/** What `trackCorrections` booked — for counting per text. */
export interface Booked {
  wrong: string
  right: string
  /** 0 = without help. See `solvedStage`. */
  stage: number
}

/**
 * Compares the findings before and after a change and records what was put
 * right.
 *
 * The way through the comparison is the only one that catches ALL corrections
 * — those through "Einsetzen" just as much as the ones typed by hand. A hook
 * on the insert button would capture only half the truth, and of all things
 * the less interesting half.
 *
 * The assignment goes through the surrounding text, not through the position:
 * on correcting, everything behind it shifts, so positions are worthless. The
 * surroundings before the mistake stay in place instead.
 */
export function trackCorrections(
  oldFindings: Finding[] | null | undefined,
  oldText: string | null | undefined,
  newFindings: Finding[],
  newText: string,
  shown: Set<string>
): Booked[] {
  const booked: Booked[] = []
  if (!oldFindings?.length || oldText == null) return booked
  // When another document is loaded, everything is different — those are not
  // corrections, that is another text.
  if (Math.abs(newText.length - oldText.length) > 200) return booked

  const stillThere = new Set(newFindings.map((e) => keyOf(e, newText)))

  for (const old of oldFindings) {
    // Only what was visibly underlined. Everything else is a typing slip in
    // passing, not a correction.
    const from = origins.get(old.word.toLowerCase()) ?? old
    if (!shown.has(old.word.toLowerCase()) && !shown.has(from.word.toLowerCase())) continue
    if (stillThere.has(keyOf(old, oldText))) continue   // still wrong

    // A comma or a space that was set is a correction too — and by far the
    // most frequent one. It changes no word, so the comparison below would
    // never see it: there the letters stay as they are, only one character
    // comes in between.
    const place = insertionAt(old)
    if (place) {
      // The finding being gone is NOT enough as evidence: it also disappears
      // when the whole text is exchanged. What counts is that the corrected
      // text really stands there now — otherwise the list fills with
      // corrections from a document that is no longer open at all.
      const target = withInsertion(old.word, place)
      if (!newText.includes(target)) continue
      store.recordCorrection(old.word, target, solvedStage(old), old.rule,
                             sentenceAround(oldText, old.start))
        .catch(() => {})
      booked.push({ wrong: old.word, right: target, stage: solvedStage(old) })
      shown.delete(old.word.toLowerCase())
      helperStages.delete(old.word.toLowerCase())
      continue
    }

    const now = wordAtSamePlace(old, oldText, newText)
    if (!now || now === old.word) continue
    // Only when a word came out of it that is no longer objected to. If it
    // is still objected to, it is a step on the way — see `origins`.
    if (newFindings.some((e) => e.word === now)) {
      origins.set(now.toLowerCase(), from)
      continue
    }
    origins.delete(old.word.toLowerCase())
    // Back where it started: „baum", „aum", „baum" is no correction.
    if (now === from.word) continue

    const stage = Math.max(solvedStage(old), solvedStage(from))
    // The sentence from the OLD text: there the word still stands wrong, and
    // exactly so it must turn up again in the exercise.
    store.recordCorrection(from.word, now, stage, from.rule,
                           sentenceAround(oldText, old.start)).catch(() => {})
    booked.push({ wrong: from.word, right: now, stage })
    shown.delete(from.word.toLowerCase())
    helperStages.delete(from.word.toLowerCase())
    // The evidence is used up: if the word appears again later, it must have
    // been visibly marked again in order to count once more.
    shown.delete(old.word.toLowerCase())
    helperStages.delete(old.word.toLowerCase())
  }
  return booked
}

/**
 * The sentence around a place.
 *
 * The same rule as on the server side for the photographed pages, so that
 * both sources have the same shape — only by position instead of by word,
 * because here it is known where the finding stood.
 */
export function sentenceAround(text: string, at: number): string {
  const a = Math.max(text.lastIndexOf('.', at), text.lastIndexOf('\n', at)) + 1
  let z = text.length
  for (const c of ['.', '\n']) {
    const j = text.indexOf(c, at)
    if (j >= 0 && j < z) z = j + (c === '.' ? 1 : 0)
  }
  return text.slice(a, z).trim()
}

/** The marked text with the missing character put in. */
export function withInsertion(word: string, place: Insertion): string {
  return word.slice(0, place.offset) + place.char + word.slice(place.offset)
}

/** A finding, identified by its surroundings instead of by its position. */
export function keyOf(e: Finding, text: string): string {
  return `${e.word}\u0000${text.slice(Math.max(0, e.start - 12), e.start)}`
}

/**
 * What stands now where the wrong word stood before?
 *
 * The search goes through the unchanged text BEFORE the place of the mistake:
 * it ends exactly where the word begins. What stands from there up to the
 * next word boundary is what became of it.
 */
export function wordAtSamePlace(
  old: Finding,
  oldText: string,
  newText: string
): string | null {
  const before = oldText.slice(Math.max(0, old.start - 24), old.start)
  // Short surroundings are ambiguous — better to count nothing then.
  if (before.length < 3 && old.start > 3) return null

  const i = old.start === 0 ? 0 : newText.indexOf(before)
  if (i < 0) return null
  // Ambiguous when the surroundings occur more than once.
  if (old.start > 0 && newText.indexOf(before, i + 1) >= 0) return null

  const from = old.start === 0 ? 0 : i + before.length
  const m = /^[\p{L}\p{N}]+(?:[’'-][\p{L}\p{N}]+)*/u.exec(newText.slice(from))
  // A match of this pattern always has group 0.
  return m ? m[0]! : null
}

/**
 * How far did the help go when this word was solved?
 *
 * `helperStages` remembers per word the highest stage that was open last.
 * Without an entry Elmar managed it without help — the normal case, the one
 * you want to see.
 */
export function solvedStage(e: Finding): number {
  const s = helperStages.get(e.word.toLowerCase())
  return s == null ? 0 : s
}

/** Counting words — without the checking service, this must work offline. */
export function* wordsOf(
  text: string
): Generator<{ word: string; start: number; end: number }> {
  const re = /[\p{L}\p{N}]+(?:[’'-][\p{L}\p{N}]+)*/gu
  let m
  // A match of this pattern always has group 0.
  while ((m = re.exec(text))) yield { word: m[0]!, start: m.index, end: m.index + m[0]!.length }
}

/**
 * There is no counter any more.
 *
 * "25 Wörter zum Anschauen" was a number in red, permanently in the bar. It
 * said how much is wrong, before Elmar had looked at a single word — and
 * exactly that is the opposite of what the app wants: the marks stand in the
 * text, one looks at them where they are. A tally at the top makes a grade
 * out of the text.
 *
 * The marks themselves say everything that is needed: where something is,
 * and of what kind.
 */
